import { Box, CollapsibleSection, Loader } from '@/common/components';
import { TFunction } from 'i18next';
import SectionSubtitle from '../../../shared/SectionSubtitle/SectionSubtitle';
import { CampaignTrackingOverviewData } from '@/modules/study-insights/services/campaign/tracking';
import { StackedBarChart, DateGroupOptionId } from './components/StackedBarChart';
import { useMemo } from 'react';
import { styled } from '@/stitches.config';
import { ResponsiveContainer } from 'recharts';
import { numberFormattedWithCommas } from '@/common/utils/number';

const ChartContainer = styled('div', {
  padding: '$6',
  backgroundColor: '$white',
  // Using fixed pixels because Chart is not responsive
  // However, we can use the ResponsiveContainer from recharts to make it responsive
  // We just need to have a parent container with a fixed height
  minHeight: '520px',
});

interface CampaignOverviewProps {
  t: TFunction;
  isLoading?: boolean;
  campaignTrackingOverviewData?: CampaignTrackingOverviewData;
  onDateGroupOptionChange: (value: DateGroupOptionId) => void;
  selectedDateGroupOption: DateGroupOptionId;
}

export const CampaignOverview = ({
  t,
  isLoading,
  campaignTrackingOverviewData,
  onDateGroupOptionChange,
  selectedDateGroupOption,
}: CampaignOverviewProps): JSX.Element => {
  const stackedBarCharData = useMemo(() => {
    if (!campaignTrackingOverviewData?.data.dateSeries) {
      return null;
    }
    return campaignTrackingOverviewData?.data.dateSeries.map((campaign) => {
      return {
        groupValue: campaign.groupValue,
        values: Object.keys(campaign.values).map((key) => {
          return {
            id: campaign.values[key].id,
            name: campaign.values[key].name,
            value: campaign.values[key].value,
          };
        }),
      };
    });
  }, [campaignTrackingOverviewData?.data.dateSeries]);

  if (isLoading) {
    return (
      <CollapsibleSection
        css={{ padding: 0, marginBottom: '$6', paddingBottom: '$10' }}
        cssTitle={{ margin: 0, maxHeight: 'none', marginLeft: '$6', paddingTop: '$6', paddingRight: '$6' }}
        title={'Campaign Overview'}
      >
        {/* 600px is similar to the size of the component with a chart */}
        <Box css={{ height: '600px', padding: '$6' }}>
          <Loader />
        </Box>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      css={{ padding: 0, marginBottom: '$6', paddingBottom: '$10' }}
      cssTitle={{ margin: 0, maxHeight: 'none', padding: '$6' }}
      title={'Campaign Overview'}
    >
      <SectionSubtitle
        isLoading={!!isLoading}
        labelLeftTop={t('participant_recruitment.total_consents')}
        labelLeftBottom={t('participant_recruitment.from_all_campaigns')}
        labelRightTop={t('participant_recruitment.total_conversion')}
        labelRightBottom={t('participant_recruitment.rate_from_all_campaigns')}
        parenthesisSubtitle=""
        numberLeft={
          campaignTrackingOverviewData?.overView.totalConsented
            ? numberFormattedWithCommas(campaignTrackingOverviewData?.overView.totalConsented)
            : '-'
        }
        numberRight={
          campaignTrackingOverviewData?.overView.totalConversionRate
            ? String(campaignTrackingOverviewData?.overView.totalConversionRate)
            : '-'
        }
      />
      {stackedBarCharData && stackedBarCharData.length > 0 && (
        <ResponsiveContainer height="100%">
          <ChartContainer>
            <StackedBarChart
              data={stackedBarCharData}
              onDateGroupOptionChange={onDateGroupOptionChange}
              selectedDateGroupOption={selectedDateGroupOption}
            />
          </ChartContainer>
        </ResponsiveContainer>
      )}
    </CollapsibleSection>
  );
};
